import type {Context} from '../types';
import {
  removeLegacyDynBindField,
  addUniversalBindingsBinding,
  createNewCMSBinding,
  isBoundToCMS,
  getBindingData,
} from '../utils/legacyBindings';
import {omit} from '@packages/utilities/key-map';
import {type Expression} from '@packages/systems/wfdl-ast';

const videoLegacyBindingMigrationData = (
  context: Context
): Expression | false => {
  const {jsonNode} = context.node;
  const {data = {}} = jsonNode;

  const isVideo = jsonNode.type === 'Video';
  if (!isVideo) {
    return false;
  }

  const dynInnerHTML = data?.dyn?.bind?.innerHTML;
  if (!dynInnerHTML) {
    return false;
  }
  // Get the slug in Universal Bindings format
  const bindingData = getBindingData(dynInnerHTML);
  if (bindingData === false) {
    return false;
  }

  const isCMsBound = isBoundToCMS(context);
  if (isCMsBound === false) {
    return false;
  }

  const videoBindingUB = createNewCMSBinding(
    bindingData.type,
    'video',
    bindingData.slug
  );

  if (videoBindingUB === null) {
    return false;
  }

  return videoBindingUB;
};

export default (context: Context) => {
  const {jsonNode} = context.node;
  const {data = {}} = jsonNode;

  const bindingData = videoLegacyBindingMigrationData(context);
  const needed = bindingData !== false;

  return {
    needed,
    logData: {
      msg: 'Convert Video CMS bindings to Universal Bindings',
      meta: {
        nodeId: jsonNode._id,
      },
    },
    apply: () => {
      // This is already handled in the needed check but the type
      // does not get refined by it
      if (bindingData === false) {
        return;
      }

      const dataDynBind = removeLegacyDynBindField('innerHTML', data.dyn.bind);

      if (dataDynBind === null) {
        const dynKeys = Object.keys(data.dyn);
        // If we only have one key in `data.dyn` we want to remove the whole
        // object
        if (dynKeys.length === 1 && dynKeys[0] === 'bind') {
          // eslint-disable-next-line webflow/no-delete
          delete data.dyn;
        } else {
          data.dyn = omit(['bind'])(data.dyn);
        }
      } else {
        data.dyn.bind = dataDynBind;
      }
      // eslint-disable-next-line webflow/no-delete
      delete data.video;

      jsonNode.bind =
        addUniversalBindingsBinding(
          'video',
          bindingData,
          jsonNode.bind || null
        ) || undefined;
    },
  };
};
